import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtMXN, fmtTime, fmtDateOnly } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OrderStateBadge from "@/components/OrderStateBadge";
import { Search, FilterX, ChevronLeft, ChevronRight, User } from "lucide-react";

const PAGE_SIZE = 25;
const OPERATIVE_ROLES = ["MESERO", "COCINA", "BEBIDAS", "FREIDORA"];

export default function OrdenesList() {
  const { user } = useAuth();
  const isOperative = OPERATIVE_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("ALL");
  const [tipo, setTipo] = useState("ALL");
  const [cobrada, setCobrada] = useState("ALL");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [skip, setSkip] = useState(0);
  const [debouncedQ, setDebouncedQ] = useState(q);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit: PAGE_SIZE };
      if (estado !== "ALL") params.estado = estado;
      if (tipo !== "ALL") params.tipo = tipo;
      if (cobrada !== "ALL") params.cobrada = cobrada === "true";
      if (debouncedQ.trim()) params.q = debouncedQ.trim();
      if (fechaDesde) params.fecha_desde = new Date(fechaDesde).toISOString();
      if (fechaHasta) {
        const fh = new Date(fechaHasta);
        fh.setHours(23, 59, 59, 999);
        params.fecha_hasta = fh.toISOString();
      }
      const r = await api.get("/ordenes", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } finally { setLoading(false); }
  }, [estado, tipo, cobrada, debouncedQ, fechaDesde, fechaHasta, skip]);

  useEffect(() => { load(); }, [load]);

  // Real-time: cuando se crea una orden nueva, o cuando una existente cambia
  // estado (cobrada, items, etc.), recargamos para que la nueva aparezca en
  // la primera posición y los estados se reflejen al instante.
  useRealtimeEvents(
    (msg) => {
      if (typeof msg?.type === "string" && msg.type.startsWith("orden.")) {
        load();
      }
    },
    () => { load(); },
  );

  // Reset skip when filters change
  useEffect(() => { setSkip(0); }, [estado, tipo, cobrada, debouncedQ, fechaDesde, fechaHasta]);

  const clearFilters = () => {
    setQ(""); setEstado("ALL"); setTipo("ALL"); setCobrada("ALL"); setFechaDesde(""); setFechaHasta(""); setSkip(0);
  };

  const hasFilters = q || estado !== "ALL" || tipo !== "ALL" || cobrada !== "ALL" || fechaDesde || fechaHasta;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-black tracking-tight">Órdenes</h2>
        <p className="text-sm text-muted-foreground mt-1">Búsqueda avanzada por número, mesa, cliente, teléfono o usuario que la creó.</p>
      </div>

      <div className="space-y-3">
        {/* Fila 1: búsqueda + selects */}
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 items-start">
          <div className="relative sm:col-span-2 lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Buscar por número, mesa, cliente, teléfono o creador…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-11"
              data-testid="ordenes-search-input"
            />
          </div>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="h-11" data-testid="filter-tipo"><SelectValue /></SelectTrigger>
            <SelectContent className="z-[60]">
              <SelectItem value="ALL">Todos los tipos</SelectItem>
              <SelectItem value="MESA">Mesa (ORD)</SelectItem>
              <SelectItem value="LLEVAR">Para llevar (TAKE)</SelectItem>
              <SelectItem value="PUBLICO">Pedido web (PUB)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="h-11" data-testid="filter-estado"><SelectValue /></SelectTrigger>
            <SelectContent className="z-[60]">
              <SelectItem value="ALL">Todos los estados</SelectItem>
              {["CREADA", "EN_PREPARACION", "PARCIALMENTE_PREPARADA", "LISTA", "ENTREGADA", "ENTREGA_PENDIENTE", "COBRADA", "CANCELADA", "DEVUELTA"].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={cobrada} onValueChange={setCobrada}>
            <SelectTrigger className="h-11" data-testid="filter-cobrada"><SelectValue /></SelectTrigger>
            <SelectContent className="z-[60]">
              <SelectItem value="ALL">Cobradas y pendientes</SelectItem>
              <SelectItem value="true">Solo cobradas</SelectItem>
              <SelectItem value="false">Solo pendientes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Fila 2: fechas + limpiar */}
        {(!isOperative || hasFilters) && (
          <div className="flex flex-wrap items-center gap-2">
            {!isOperative && (
              <>
                <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="h-11 w-full sm:w-[180px]" data-testid="filter-fecha-desde" />
                <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="h-11 w-full sm:w-[180px]" data-testid="filter-fecha-hasta" />
              </>
            )}
            {hasFilters && (
              <Button variant="outline" onClick={clearFilters} className="h-11" data-testid="clear-filters-btn">
                <FilterX className="h-4 w-4 mr-2" />Limpiar filtros
              </Button>
            )}
          </div>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="text-left p-3">Número</th>
                  <th className="text-left">Tipo</th>
                  <th className="text-left">Mesa / Cliente</th>
                  <th className="text-left">Registrado por</th>
                  <th className="text-left">Estado</th>
                  <th className="text-left">Cobrada</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Fecha</th>
                  <th className="text-right p-3">Hora</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="text-center text-muted-foreground py-8">Cargando…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-muted-foreground py-8">Sin resultados</td></tr>
                ) : items.map((o) => (
                  <tr key={o.id} className="border-t border-border hover:bg-secondary/40">
                    <td className="p-3 font-bold"><Link to={`/orden/${o.id}`} className="hover:text-primary" data-testid={`orden-link-${o.id}`}>{o.numero}</Link></td>
                    <td className="text-muted-foreground">{o.tipo}</td>
                    <td>{o.mesa_nombre || o.cliente_nombre || "-"}</td>
                    <td className="text-muted-foreground">
                      {o.created_by_nombre ? (
                        <span className="inline-flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {o.created_by_nombre}
                          {o.created_by_role && <span className="text-[10px] uppercase tracking-wider ml-1 opacity-70">/ {o.created_by_role}</span>}
                        </span>
                      ) : "—"}
                    </td>
                    <td><OrderStateBadge state={o.estado} /></td>
                    <td>{o.cobrada ? <span className="text-emerald-500 font-semibold">Sí</span> : <span className="text-muted-foreground">No</span>}</td>
                    <td className="text-right font-semibold">{fmtMXN(o.total)}</td>
                    <td className="text-right text-muted-foreground whitespace-nowrap" data-testid={`orden-fecha-${o.id}`}>{fmtDateOnly(o.created_at)}</td>
                    <td className="p-3 text-right text-muted-foreground whitespace-nowrap" data-testid={`orden-hora-${o.id}`}>{fmtTime(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Paginación */}
          <div className="flex items-center justify-between p-3 border-t border-border text-sm">
            <div className="text-muted-foreground">
              {total === 0 ? "0 resultados" : `Mostrando ${skip + 1}-${Math.min(skip + PAGE_SIZE, total)} de ${total}`}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={skip === 0 || loading} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="page-prev-btn">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold">{currentPage} / {totalPages}</span>
              <Button size="sm" variant="outline" disabled={skip + PAGE_SIZE >= total || loading} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="page-next-btn">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
